import { useEffect, useState, useRef } from "react";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useStoreState, useStoreActions } from "easy-peasy";
import { motion } from "motion/react";
import TwilioServices from "./TwilioServices";
import Logo from "../../images/teleforce-logo-mark.svg?react";
import { Select, MenuItem } from "@mui/material";
import { Duration } from "luxon";
import {
  sortGrades,
  sortTeams,
  sortLocations,
  sortShifts,
  sortSchedules,
  sortQueuesGroups
} from "../../utils";
import AnimatedCardValue from "../Common/AnimatedCardValue";
import ThemeToggle from "../Common/ThemeToggle";
import { red } from "@mui/material/colors";

export default function DashboardContainer() {
  const [isRefreshing, setIsRefreshing] = useState(true);
  const selectedDepartment = useStoreState(state => state.departments.selected);
  const agents = useStoreState(state => state.agents);
  const queues = useStoreState(state => state.queues);
  const departmentData = useStoreState(state => state.departments.data);
  const setSelectedDepartment = useStoreActions(
    actions => actions.departments.setSelected
  );
  const setAgentsData = useStoreActions(actions => actions.agents.setData);
  const groupingSelected = useStoreState(state => state.grouping.selected);
  const setGroupingSelected = useStoreActions(
    actions => actions.grouping.setSelected
  );

  const groupedByGrade = useStoreState(state => state.agents.groupedByGrade);
  const groupedByTeam = useStoreState(state => state.agents.groupedByTeam);
  const groupedByLocation = useStoreState(
    state => state.agents.groupedByLocation
  );
  const groupedByShift = useStoreState(state => state.agents.groupedByShift);
  const groupedBySchedule = useStoreState(
    state => state.agents.groupedBySchedule
  );
  const groupedByQueue = useStoreState(state => state.agents.groupedByQueue);
  const triggerRefresh = useStoreActions(actions => actions.refresh.trigger);
  const resetAgentData = useStoreActions(actions => actions.agents.reset);

  const handleGroupingChange = event => {
    setGroupingSelected(event.target.value);
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    resetAgentData();
    setTimeout(() => {
      triggerRefresh();
    }, 500);
  };

  useEffect(() => {
    if (isRefreshing && Object.keys(agents.data || {}).length > 0) {
      setIsRefreshing(false);
    }
  }, [agents.data, isRefreshing]);

  const getGroupedData = () => {
    switch (groupingSelected) {
      case "grade":
        return groupedByGrade;
      case "team":
        return groupedByTeam;
      case "location":
        return groupedByLocation;
      case "shift":
        return groupedByShift;
      case "schedule":
        return groupedBySchedule;
      case "queue":
        return groupedByQueue;
      default:
        return null;
    }
  };

  const getSortFunction = () => {
    switch (groupingSelected) {
      case "grade":
        return sortGrades;
      case "team":
        return sortTeams;
      case "location":
        return sortLocations;
      case "shift":
        return sortShifts;
      case "schedule":
        return sortSchedules;
      case "queue":
        return sortQueuesGroups;
      default:
        return (a, b) => a.localeCompare(b);
    }
  };

  const calculateGroupStats = agentsList => {
    const totalCalls = agentsList.reduce((sum, a) => sum + (a.calls || 0), 0);
    const totalSales = agentsList.reduce((sum, a) => sum + (a.sales || 0), 0);
    const sumOfTalkAvg = agentsList.reduce(
      (sum, a) => sum + (a.calls ? a.talkTime / a.calls : 0),
      0
    );
    const sumOfReadyAvg = agentsList.reduce(
      (sum, a) => sum + (a.calls ? a.readyTime / a.calls : 0),
      0
    );
    const sumOfWrapAvg = agentsList.reduce(
      (sum, a) => sum + (a.calls ? a.wrapTime / a.calls : 0),
      0
    );
    const offline = agentsList.filter(
      a => a.activity === "Offline" && a.grade !== "M"
    ).length;
    const online = agentsList.filter(
      a => a.activity !== "Offline" && a.grade !== "M"
    ).length;
    const talking = agentsList.filter(
      a => a.activity === "Talking" && a.grade !== "M"
    ).length;
    const ready = agentsList.filter(
      a => a.activity === "Ready" && a.grade !== "M"
    ).length;
    const wrapUp = agentsList.filter(
      a => a.activity === "Wrap-Up" && a.grade !== "M"
    ).length;
    const notReady = agentsList.filter(
      a => a.activity === "Not Ready" && a.grade !== "M"
    ).length;
    const breakCount = agentsList.filter(
      a =>
        ["Break 15", "Break 30", "Break 45", "Break 60"].includes(a.activity) &&
        a.grade !== "M"
    ).length;
    const meeting = agentsList.filter(
      a => a.activity === "Meeting" && a.grade !== "M"
    ).length;
    const observing = agentsList.filter(
      a => a.activity === "Observing" && a.grade !== "M"
    ).length;

    const inactivePercent =
      online > 0 ? Math.round(((notReady + breakCount) / online) * 100) : 0;

    return {
      offline,
      online,
      talking,
      ready,
      wrapUp,
      notReady,
      break: breakCount,
      meeting,
      observing,
      totalCalls,
      totalSales,
      closingPercent: totalCalls > 0 ? (totalSales / totalCalls) * 100 : 0,
      avgTalkTime: agentsList.length > 0 ? sumOfTalkAvg / agentsList.length : 0,
      avgReadyTime:
        agentsList.length > 0 ? sumOfReadyAvg / agentsList.length : 0,
      avgWrapTime: agentsList.length > 0 ? sumOfWrapAvg / agentsList.length : 0,
      inactivePercent
    };
  };

  const formatTime = seconds =>
    Duration.fromObject({ seconds: seconds || 0 }).toFormat("mm:ss");

  const handleDepartment = event => {
    if (event.target.value !== selectedDepartment.id) {
      setAgentsData({ event: "reset" });
      setSelectedDepartment(departmentData[event.target.value]);
    }
  };

  return (
    <Root>
      <TwilioServices />
      <StyledDashboard>
        <StyledHeader>
          <StyledLogo />

          <StyledSelect
            value={selectedDepartment.id}
            onChange={handleDepartment}
            MenuProps={{
              PaperProps: {
                sx: {
                  background: "var(--teleforce-800)",
                  borderRadius: "4px",
                  minWidth: "200px"
                }
              },
              slotProps: {
                paper: {
                  style: {
                    marginTop: 10,
                    marginLeft: -15
                  }
                }
              }
            }}
          >
            {Object.keys(departmentData).map(id => (
              <StyledMenuItem value={id} key={id}>
                {departmentData[id]["name"]}
              </StyledMenuItem>
            ))}
          </StyledSelect>
        </StyledHeader>

        <StyledSection>
          <StyledSectionHeader>
            <StyledGroupingSelect
              value={groupingSelected || ""}
              onChange={handleGroupingChange}
              displayEmpty
              renderValue={selected => {
                if (!selected) {
                  return (
                    <span style={{ color: "var(--teleforce-700)" }}>
                      Select Grouping
                    </span>
                  );
                }
                // Capitalize the selected value for display
                return selected.charAt(0).toUpperCase() + selected.slice(1);
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    background: "var(--teleforce-800)",
                    borderRadius: "4px",
                    minWidth: "182px"
                  }
                },
                slotProps: {
                  paper: {
                    style: {
                      marginTop: 10
                    }
                  }
                }
              }}
            >
              <StyledMenuItem value="">None</StyledMenuItem>
              <StyledMenuItem value="grade">Grade</StyledMenuItem>
              <StyledMenuItem value="team">Team</StyledMenuItem>
              <StyledMenuItem value="location">Location</StyledMenuItem>
              <StyledMenuItem value="shift">Shift</StyledMenuItem>
              <StyledMenuItem value="schedule">Schedule</StyledMenuItem>
              <StyledMenuItem value="queue">Queue</StyledMenuItem>
            </StyledGroupingSelect>
          </StyledSectionHeader>

          <StyledSectionStats>
            <StyledSectionStatsHeading>Queue</StyledSectionStatsHeading>
            <StyledSectionBody>
              <StyledCard warning={queues.callsWaiting > 10}>
                <StyledCardHeading>Waiting</StyledCardHeading>
                <AnimatedCardValue value={queues.callsWaiting} format={true} />
              </StyledCard>

              <StyledCard>
                <StyledCardHeading>Drops</StyledCardHeading>
                <AnimatedCardValue value={queues.totalDrops} format={true} />
              </StyledCard>
            </StyledSectionBody>
          </StyledSectionStats>

          {!groupingSelected ? (
            <>
              <StyledSectionStats>
                <StyledSectionStatsHeading>
                  Performance
                </StyledSectionStatsHeading>
                <StyledSectionBody>
                  <StyledCard>
                    <StyledCardHeading>Calls</StyledCardHeading>
                    <AnimatedCardValue
                      value={agents.totalCalls}
                      format={true}
                    />
                  </StyledCard>

                  {selectedDepartment?.code !== "support-customer" &&
                    selectedDepartment?.code !== "support-device" && (
                      <>
                        <StyledCard>
                          <StyledCardHeading>Sales</StyledCardHeading>
                          <AnimatedCardValue
                            value={agents.totalSales}
                            format={true}
                          />
                        </StyledCard>
                        <StyledCard>
                          <StyledCardHeading>Closing %</StyledCardHeading>
                          <AnimatedCardValue
                            value={agents.totalSalesPercent}
                            decimals={2}
                            suffix="%"
                            animateDown={true}
                          />
                        </StyledCard>
                      </>
                    )}

                  <StyledCard>
                    <StyledCardHeading>Talk Time Avg</StyledCardHeading>
                    <StyledCardValue>
                      {formatTime(agents.avgTalkTime)}
                    </StyledCardValue>
                  </StyledCard>
                  <StyledCard>
                    <StyledCardHeading>Ready Time Avg</StyledCardHeading>
                    <StyledCardValue>
                      {formatTime(agents.avgReadyTime)}
                    </StyledCardValue>
                  </StyledCard>
                  <StyledCard>
                    <StyledCardHeading>Wrap Time Avg</StyledCardHeading>
                    <StyledCardValue>
                      {formatTime(agents.avgWrapTime)}
                    </StyledCardValue>
                  </StyledCard>
                </StyledSectionBody>
              </StyledSectionStats>
              <StyledSectionStats>
                <StyledSectionStatsHeading>
                  Activity Stats
                </StyledSectionStatsHeading>
                <StyledSectionBody>
                  <StyledCard>
                    <StyledCardHeading>Online</StyledCardHeading>
                    <AnimatedCardValue value={agents.online} format={true} />
                  </StyledCard>
                  <StyledCard>
                    <StyledCardHeading>Talking</StyledCardHeading>
                    <AnimatedCardValue value={agents.talking} format={true} />
                  </StyledCard>
                  <StyledCard>
                    <StyledCardHeading>Ready</StyledCardHeading>
                    <AnimatedCardValue value={agents.ready} format={true} />
                  </StyledCard>
                  <StyledCard>
                    <StyledCardHeading>Wrap-Up</StyledCardHeading>
                    <AnimatedCardValue value={agents.wrapUp} format={true} />
                  </StyledCard>
                  <StyledCard>
                    <StyledCardHeading>Not Ready</StyledCardHeading>
                    <AnimatedCardValue value={agents.notReady} format={true} />
                  </StyledCard>
                  <StyledCard>
                    <StyledCardHeading>Break</StyledCardHeading>
                    <AnimatedCardValue value={agents.break} format={true} />
                  </StyledCard>
                  <StyledCard>
                    <StyledCardHeading>Meeting</StyledCardHeading>
                    <AnimatedCardValue value={agents.meeting} format={true} />
                  </StyledCard>
                  <StyledCard>
                    <StyledCardHeading>Observing</StyledCardHeading>
                    <AnimatedCardValue value={agents.observing} format={true} />
                  </StyledCard>
                  <StyledCard>
                    <StyledCardHeading>Inactive %</StyledCardHeading>
                    <AnimatedCardValue
                      value={agents.inactivePercent}
                      suffix="%"
                      animateDown={true}
                    />
                  </StyledCard>
                  <StyledCard>
                    <StyledCardHeading>Offline</StyledCardHeading>
                    <AnimatedCardValue value={agents.offline} format={true} />
                  </StyledCard>
                </StyledSectionBody>
              </StyledSectionStats>
            </>
          ) : (
            <StyledAccordionContainer>
              {Object.entries(getGroupedData())
                .sort(([a], [b]) => getSortFunction()(a, b))
                .map(([groupKey, agentsList]) => {
                  const stats = calculateGroupStats(agentsList);
                  return (
                    <StyledAccordion key={groupKey}>
                      <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <StyledAccordionTitle>
                          {groupKey}
                          <StyledAccordionCount>
                            ({agentsList.length})
                          </StyledAccordionCount>
                        </StyledAccordionTitle>
                      </StyledAccordionSummary>
                      <StyledAccordionDetails>
                        <StyledSectionStats>
                          <StyledSectionStatsHeading>
                            Performance
                          </StyledSectionStatsHeading>
                          <StyledSectionBody>
                            {/* Performance Stats */}
                            <StyledCard>
                              <StyledCardHeading>Calls</StyledCardHeading>
                              <AnimatedCardValue
                                value={stats.totalCalls}
                                format={true}
                              />
                            </StyledCard>

                            {selectedDepartment?.code !== "support-customer" &&
                              selectedDepartment?.code !== "support-device" && (
                                <>
                                  <StyledCard>
                                    <StyledCardHeading>Sales</StyledCardHeading>
                                    <AnimatedCardValue
                                      value={stats.totalSales}
                                      format={true}
                                    />
                                  </StyledCard>
                                  <StyledCard>
                                    <StyledCardHeading>
                                      Closing %
                                    </StyledCardHeading>
                                    <AnimatedCardValue
                                      value={stats.closingPercent}
                                      decimals={2}
                                      suffix="%"
                                      animateDown={true}
                                    />
                                  </StyledCard>
                                </>
                              )}

                            <StyledCard>
                              <StyledCardHeading>
                                Talk Time Avg
                              </StyledCardHeading>
                              <StyledCardValue>
                                {formatTime(stats.avgTalkTime)}
                              </StyledCardValue>
                            </StyledCard>
                            <StyledCard>
                              <StyledCardHeading>
                                Ready Time Avg
                              </StyledCardHeading>
                              <StyledCardValue>
                                {formatTime(stats.avgReadyTime)}
                              </StyledCardValue>
                            </StyledCard>
                            <StyledCard>
                              <StyledCardHeading>
                                Wrap Time Avg
                              </StyledCardHeading>
                              <StyledCardValue>
                                {formatTime(stats.avgWrapTime)}
                              </StyledCardValue>
                            </StyledCard>
                          </StyledSectionBody>
                        </StyledSectionStats>
                        <StyledSectionStats>
                          <StyledSectionStatsHeading>
                            Activity Stats
                          </StyledSectionStatsHeading>
                          <StyledSectionBody>
                            {/* Activity Stats */}
                            <StyledCard>
                              <StyledCardHeading>Online</StyledCardHeading>
                              <AnimatedCardValue
                                value={stats.online}
                                format={true}
                              />
                            </StyledCard>
                            <StyledCard>
                              <StyledCardHeading>Talking</StyledCardHeading>
                              <AnimatedCardValue
                                value={stats.talking}
                                format={true}
                              />
                            </StyledCard>
                            <StyledCard>
                              <StyledCardHeading>Ready</StyledCardHeading>
                              <AnimatedCardValue
                                value={stats.ready}
                                format={true}
                              />
                            </StyledCard>
                            <StyledCard>
                              <StyledCardHeading>Wrap-Up</StyledCardHeading>
                              <AnimatedCardValue
                                value={stats.wrapUp}
                                format={true}
                              />
                            </StyledCard>
                            <StyledCard>
                              <StyledCardHeading>Not Ready</StyledCardHeading>
                              <AnimatedCardValue
                                value={stats.notReady}
                                format={true}
                              />
                            </StyledCard>
                            <StyledCard>
                              <StyledCardHeading>Break</StyledCardHeading>
                              <AnimatedCardValue
                                value={stats.break}
                                format={true}
                              />
                            </StyledCard>
                            <StyledCard>
                              <StyledCardHeading>Meeting</StyledCardHeading>
                              <AnimatedCardValue
                                value={stats.meeting}
                                format={true}
                              />
                            </StyledCard>
                            <StyledCard>
                              <StyledCardHeading>Observing</StyledCardHeading>
                              <AnimatedCardValue
                                value={stats.observing}
                                format={true}
                              />
                            </StyledCard>
                            <StyledCard>
                              <StyledCardHeading>Inactive %</StyledCardHeading>
                              <AnimatedCardValue
                                value={stats.inactivePercent}
                                suffix="%"
                                animateDown={true}
                              />
                            </StyledCard>
                            <StyledCard>
                              <StyledCardHeading>Offline</StyledCardHeading>
                              <AnimatedCardValue
                                value={stats.offline}
                                format={true}
                              />
                            </StyledCard>
                          </StyledSectionBody>
                        </StyledSectionStats>
                      </StyledAccordionDetails>
                    </StyledAccordion>
                  );
                })}
            </StyledAccordionContainer>
          )}
        </StyledSection>
      </StyledDashboard>

      <ThemeToggle />

      <StyledRefresh onClick={handleRefresh}>
        <motion.div
          key={isRefreshing ? "spinning" : "idle"}
          animate={{ rotate: isRefreshing ? 360 : 0 }}
          transition={{
            duration: isRefreshing ? 0.6 : 0,
            repeat: isRefreshing ? Infinity : 0,
            ease: "linear"
          }}
          style={{ display: "flex" }}
        >
          <StyledRefreshIcon />
        </motion.div>
      </StyledRefresh>
    </Root>
  );
}

const PREFIX = "DashboardContainer";

const classes = {
  screenWithDetail: `${PREFIX}-screenWithDetail`,
  screenFull: `${PREFIX}-screenFull`
};

const Root = styled("div")(() => ({
  [`& .${classes.screenWithDetail}`]: {
    width: "calc(100% - 240px)",
    transition: "all .2s ease-in-out"
  },

  [`& .${classes.screenFull}`]: {
    width: "100%",
    transition: "all .2s ease-in-out"
  }
}));

const StyledCard = styled("div")(({ warning }) => ({
  padding: 18,
  background: warning ? red[600] : "var(--teleforce-975)",
  borderRadius: 10,
  width: "100%",
  height: "auto",
  display: "flex",
  flexDirection: "column",
  gap: ".5rem",

  ...(warning && {
    "& > div": {
      color: "#fff"
    }
  })
}));

const StyledCardHeading = styled("div")({
  color: "var(--teleforce-700)",
  fontSize: 12
});

const StyledCardValue = styled("div")(({ theme }) => ({
  color: "var(--teleforce-50)",
  fontSize: "1.7rem",
  lineHeight: 1,
  fontFamily: "Roboto Mono, sans-serif",

  [theme.breakpoints.up("md")]: {
    fontSize: "1.8rem"
  }
}));

const StyledDashboard = styled("div")({
  margin: "0 auto",
  padding: "2rem",
  maxWidth: 1400,
  height: "100%"
});

const StyledSection = styled("div")({
  paddingBottom: "4rem"
});

const StyledSectionHeader = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "0",
  marginTop: "0rem"
});

const StyledSectionBody = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)", // 2 columns on mobile
  gap: "1rem",

  // Switch to auto-fit for larger screens
  [theme.breakpoints.up("md")]: {
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))"
  }
}));

const StyledSelect = styled(Select)({
  fontSize: "1.2rem",
  fontWeight: "bold",
  border: "none",
  borderRadius: 50,
  marginLeft: "auto",

  "& .MuiSelect-select": {
    padding: "8px 40px 9px 22px !important"
  }
});

const StyledMenuItem = styled(MenuItem)({
  paddingLeft: "35px",
  paddingRight: "35px",
  minHeight: "48px !important",
  "&.Mui-selected": {
    display: "none"
  }
});

const StyledHeader = styled("div")({
  display: "flex",
  alignItems: "center",
  // justifyContent: "space-between",
  padding: "0"
});

const StyledLogo = styled(Logo)({
  '& [fill]:not([fill="none"])': {
    fill: "var(--teleforce-50)"
  }
});

const StyledGroupingSelect = styled(Select)(({ theme }) => ({
  fontSize: ".89rem",
  fontWeight: "normal",
  border: "0 !important",
  borderRadius: 25,
  margin: "3rem 0 1rem 0",
  minWidth: "100%",
  background: "var(--teleforce-975)",

  [theme.breakpoints.up("md")]: {
    minWidth: 300
  },

  "& .MuiSelect-select": {
    padding: ".85rem 1.25rem",
    border: 0
  },

  "& .MuiOutlinedInput-notchedOutline": {
    border: 0
  }
}));

const StyledAccordionContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  marginTop: "1.75rem"
});

const StyledAccordion = styled(Accordion)({
  background: "var(--teleforce-975)",
  boxShadow: "none",
  borderRadius: "10px !important",
  "&:before": {
    display: "none"
  },
  "&.Mui-expanded": {
    margin: 0
  }
});

const StyledAccordionSummary = styled(AccordionSummary)({
  background: "var(--teleforce-975)",
  borderRadius: 10,
  minHeight: "64px !important",
  "& .MuiAccordionSummary-content": {
    margin: "16px 0"
  }
});

const StyledAccordionTitle = styled("div")({
  fontSize: "1.5rem",
  color: "var(--teleforce-50)",
  fontWeight: 500,
  display: "flex",
  alignItems: "center",
  gap: "0.5rem"
});

const StyledAccordionCount = styled("span")({
  color: "var(--teleforce-700)",
  fontSize: 14,
  fontWeight: 400
});

const StyledAccordionDetails = styled(AccordionDetails)({
  background: "var(--teleforce-app-bg)",
  padding: "0 0"
});

const StyledSectionStatsHeading = styled("div")({
  fontSize: ".89rem",
  color: "var(--teleforce-700)",
  margin: "1.75rem 0 0 0",
  fontWeight: 200,
  width: "100%"
});

const StyledSectionStats = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "1rem"
});

const StyledRefresh = styled("div")({
  background: "var(--teleforce-750)",
  borderRadius: 50,
  padding: "1rem",
  display: "inline-flex",
  position: "fixed",
  bottom: "1rem",
  right: "2rem"
});

const StyledRefreshIcon = styled(RefreshIcon)({
  fontSize: 25,
  color: "var(--teleforce-50)",
  cursor: "pointer"
});
